import type { RouteRecordRaw } from 'vue-router'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

/**
 * Japan-specific product routes (drawers / webviews).
 *
 * Owned by the APAC / Global Growth team. Co-located with the JP page
 * composition so the team can add or modify JP routes without depending on
 * the PDP team. See `product-core/README.md` "Routes" section and
 * https://backmarket.atlassian.net/browse/PPI-7883.
 *
 * Always returns the full route list on every market. Non-JP markets are
 * gated via `meta.routeExists: () => isJapanMarket` (404 on non-JP) instead
 * of an early conditional return, so the translations split-v2 build script
 * (which stubs `useMarketplace` to US) statically discovers every route and
 * writes it into hash-by-route.json / keys-by-hash.json. A conditional `[]`
 * return would drop these routes from the artifacts and break JP drawer
 * translations in prod. See docs/routing/how-to/how-to-add-conditional-routes.md.
 */
export default function jpProductRoutes(): RouteRecordRaw[] {
  const { market } = useMarketplace()

  const isJapanMarket = market.countryCode === MarketCountryCode.JP

  return [
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/VerifiedRefurbishedModal/components/VerifiedRefurbishedModalBody/VerifiedRefurbishedModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_VERIFIED_REFURBISHED_DRAWER,
      path: 'p/drawers/verified-refurbished',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/PaypayModal/components/PaypayModalBody/PaypayModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_PAYPAY_DRAWER,
      path: 'p/drawers/paypay-points',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/SimModal/components/SimModalBody/SimModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_SIM_DRAWER,
      path: 'p/drawers/sim',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/SimUnlockedModal/components/SimUnlockedModalBody/SimUnlockedModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_SIM_UNLOCKED_DRAWER,
      path: 'p/drawers/sim-unlocked',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/AboutBatteryModal/components/AboutBatteryModalBody/AboutBatteryModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_ABOUT_BATTERY_DRAWER,
      path: 'p/drawers/battery',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/AboutBatteryModalThreeLevels/components/AboutBatteryModalThreeLevelsBody/AboutBatteryModalThreeLevelsBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_JP_ABOUT_BATTERY_THREE_LEVELS_DRAWER,
      path: 'p/drawers/battery-three-levels',
      meta: {
        layout: false,
        owners: ['bot-squad-global-growth-front'],
        routeExists: () => isJapanMarket,
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
  ]
}
